import { SettingOutlined } from '@ant-design/icons'
import type { EltModelFieldsFragment } from '@salescore/client-api'
import {
  HUB_PROPERTY_TYPE_LABEL,
  PROPERTY_META_LABEL,
  PropertyMetaTag,
  PropertyTypeTag,
} from '@salescore/client-common'
import type { ModelProperty } from '@salescore/core'
import { CodeHighlighter, getColumnSearchProps } from '@salescore/frontend-common'
import { App, Button, Table, Tag } from 'antd'
import { Order, String } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import type { ReactElement } from 'react'

const ellipsisFilter = (string_: unknown, x: number): string => {
  if (typeof string_ !== 'string') {
    return ''
  }

  if (string_.length <= x) {
    return string_
  }
  return `${string_.slice(0, x)}...`
}

/**
 * 型カラムの sorter。表示と同じくラベルで比較する。
 *
 * `model` は CoreModel スカラー経由で届くが serialize が無検証 (`apps/backend/web/src/graphql/scalars/CoreModel.ts`)
 * なので、生成型に無い type が実行時に来るとラベルが引けず undefined になる。
 *
 * Why not `?? x.type`: raw 値へフォールバックすると、表示側 (`PropertyTypeTag` はラベルが引けないと空の Tag を返す)
 * とソートキーが食い違う。値なしとして扱い nullable 側で受ける。
 */
export const compareModelPropertiesByType = Order.mapInput(
  Order.make<string | null | undefined>((a, b) =>
    // eslint-disable-next-line sonarjs/no-nested-conditional
    isNotNullish(a) ? (isNotNullish(b) ? String.localeCompare(b)(a) : -1) : isNotNullish(b) ? 1 : 0,
  ),
  (x: Pick<ModelProperty, 'type'>) => HUB_PROPERTY_TYPE_LABEL[x.type],
)

/** 選択肢カラムの sorter。表示と同じく label を連結して比較する (要素はオブジェクトなので素の join では並ばない) */
export const compareModelPropertiesBySelectOptions = Order.mapInput(
  Order.make<string>((a, b) => String.localeCompare(b)(a)),
  (x: Pick<ModelProperty, 'selectOptions'>) => (x.selectOptions ?? []).map((option) => option.label).join(','),
)

/** 参照先カラムの sorter。表示と同じく modelName を連結して比較する */
export const compareModelPropertiesByReferenceTo = Order.mapInput(
  Order.make<string>((a, b) => String.localeCompare(b)(a)),
  (x: Pick<ModelProperty, 'referenceTo'>) => (x.referenceTo ?? []).map((reference) => reference.modelName).join(','),
)

export const ModelPropertiesModalContent = ({
  eltModel,
}: {
  eltModel: EltModelFieldsFragment | undefined
}): ReactElement => {
  const { modal } = App.useApp()

  return (
    <div className="overflow-x-scroll">
      <Table
        dataSource={eltModel?.model.properties ?? []}
        pagination={false}
        style={{ width: '100%' }}
        columns={[
          {
            title: t('表示名'),
            dataIndex: 'description',
            key: 'description',
            width: 250,
            sorter: Order.mapInput(
              Order.make<string>((a, b) => String.localeCompare(b)(a)),
              (x: ModelProperty) => x.label,
            ),
            render(_value, record) {
              return <div>{ellipsisFilter(record.label, 35)}</div>
            },
            ...getColumnSearchProps((record: ModelProperty) => record.label),
          },
          {
            title: <div className="">内部名</div>,
            dataIndex: 'name',
            key: 'name',
            width: 150,
            sorter: Order.mapInput(
              Order.make<string>((a, b) => String.localeCompare(b)(a)),
              (x: ModelProperty) => x.name,
            ),
            render(_value, record) {
              return <div className="text-gray-500">{ellipsisFilter(record.name, 30)}</div>
            },
            ...getColumnSearchProps((record: ModelProperty) => record.name),
          },
          {
            title: '型',
            dataIndex: 'type',
            sorter: compareModelPropertiesByType,
            render(_value: string, record) {
              return <PropertyTypeTag type={record.type} />
            },
          },
          {
            title: 'メタ情報',
            dataIndex: 'meta',
            sorter: Order.mapInput(
              Order.make<string | null | undefined>((a, b) =>
                // eslint-disable-next-line sonarjs/no-nested-conditional
                isNotNullish(a) ? (isNotNullish(b) ? String.localeCompare(b)(a) : -1) : isNotNullish(b) ? 1 : 0,
              ),
              (x: ModelProperty) => (isNotNullish(x.meta) ? PROPERTY_META_LABEL[x.meta] : x.meta),
            ),
            render(_value: string, record) {
              return <PropertyMetaTag meta={record.meta} />
            },
          },
          {
            title: '選択肢',
            dataIndex: 'selectOptions',
            sorter: compareModelPropertiesBySelectOptions,
            render(_value: string, record) {
              const options = (record.selectOptions ?? []).map((x) => x.label)
              if (options.length === 0) {
                return <span></span>
              }
              return (
                <span>
                  {options.slice(0, 3).join(',')}
                  {options.length > 3 ? `...(他${options.length - 3}件)` : ''}
                </span>
              )
            },
          },
          {
            title: '参照先',
            dataIndex: 'referenceTo',
            sorter: compareModelPropertiesByReferenceTo,
            render(_value: unknown, record) {
              return (
                <>
                  {(record.referenceTo ?? []).map((x, index) => (
                    <Tag color="default" className="px-2" key={index}>
                      {x.modelName} {x.key === undefined ? '' : `(${x.key})`}
                    </Tag>
                  ))}
                </>
              )
            },
          },
          {
            title: t('詳細'),
            dataIndex: 'all',
            render(_value, record) {
              return (
                <Button
                  icon={<SettingOutlined />}
                  onClick={() => {
                    void modal.info({
                      width: '600px',
                      title: t('詳細'),
                      content: (
                        <CodeHighlighter language="json" customStyle={{ padding: 10 }} wrapLongLines={true}>
                          {JSON.stringify(record, null, 2)}
                        </CodeHighlighter>
                      ),
                      okText: t(`閉じる`),
                    })
                  }}
                />
              )
            },
          },
        ]}
      />
    </div>
  )
}
